import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, X, Target } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function FullMatchTab({ 
  configs, 
  registeredUsers, 
  onScorecardCreated 
}) {
  const [fullMatchDate, setFullMatchDate] = useState('');
  const [fullMatchName, setFullMatchName] = useState('');
  const [fullMatchPlayers, setFullMatchPlayers] = useState([
    { id: Date.now(), name: '', division: '', score: '', manualPoints: '' }
  ]);
  const [autoCalculatePoints, setAutoCalculatePoints] = useState(true);
  const [submittingMatch, setSubmittingMatch] = useState(false);

  const addPlayerRow = () => {
    setFullMatchPlayers([...fullMatchPlayers, { id: Date.now(), name: '', division: '', score: '', manualPoints: '' }]);
  };

  const removePlayerRow = (index) => {
    if (fullMatchPlayers.length > 1) {
      setFullMatchPlayers(fullMatchPlayers.filter((_, i) => i !== index));
    }
  };

  const updatePlayerRow = (index, field, value) => {
    const updated = [...fullMatchPlayers];
    updated[index][field] = value;
    setFullMatchPlayers(updated);
  };

  const handleSubmitFullMatch = async (e) => {
    e.preventDefault();
    
    if (!fullMatchDate) {
      toast.error('Please select a competition date');
      return;
    }
    
    const validPlayers = fullMatchPlayers.filter(p => p.name.trim() && p.division);
    if (validPlayers.length === 0) {
      toast.error('Please add at least one player with name and division');
      return;
    }

    // Validate manual points if not auto-calculating
    if (!autoCalculatePoints) {
      const missingPoints = validPlayers.some(p => !p.manualPoints && p.manualPoints !== 0);
      if (missingPoints) {
        toast.error('Please enter points for all players when using manual point entry');
        return;
      }
    }

    setSubmittingMatch(true);
    
    try {
      const payload = {
        scorecard_date: fullMatchDate,
        match_name: fullMatchName || null,
        auto_calculate_points: autoCalculatePoints,
        players: validPlayers.map(p => ({
          name: p.name.trim(),
          division: p.division,
          score: parseInt(p.score) || 0,
          manual_points: autoCalculatePoints ? null : (parseInt(p.manualPoints) || 0)
        }))
      };

      await axios.post(`${API}/admin/full-match`, payload);
      
      toast.success(`Match created with ${validPlayers.length} players!`);
      
      // Reset form
      setFullMatchDate('');
      setFullMatchName('');
      setFullMatchPlayers([{ id: Date.now(), name: '', division: '', score: '', manualPoints: '' }]);
      setAutoCalculatePoints(true);
      
      // Notify parent to refresh scorecards
      if (onScorecardCreated) {
        onScorecardCreated();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    } finally {
      setSubmittingMatch(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm">
      <div className="p-6 border-b border-stone-200">
        <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight">
          Create Full Match
        </h2>
        <p className="font-inter text-stone-600 text-sm mt-1">
          Enter all players and scores for a competition at once. Points can be auto-calculated from rankings or entered manually.
        </p>
      </div>

      <form onSubmit={handleSubmitFullMatch} className="p-6 space-y-6">
        {/* Match Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="matchDate" className="font-inter font-medium text-slate-900">
              Competition Date *
            </Label>
            <Input
              id="matchDate"
              data-testid="full-match-date"
              type="date"
              value={fullMatchDate}
              onChange={(e) => setFullMatchDate(e.target.value)}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter"
              required
            />
          </div>
          <div>
            <Label htmlFor="matchName" className="font-inter font-medium text-slate-900">
              Match Name (Optional)
            </Label>
            <Input
              id="matchName"
              data-testid="full-match-name"
              type="text"
              value={fullMatchName}
              onChange={(e) => setFullMatchName(e.target.value)}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter"
              placeholder="e.g., Monthly Championship"
            />
          </div>
        </div>

        {/* Points Calculation Mode */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <Label className="font-inter font-medium text-slate-900 mb-3 block">
            Point Calculation Mode
          </Label>
          <div className="flex flex-col sm:flex-row gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="pointsMode"
                checked={autoCalculatePoints}
                onChange={() => setAutoCalculatePoints(true)}
                className="w-4 h-4 text-amber-600"
              />
              <span className="font-inter text-sm">
                <strong>Auto-calculate</strong> - Points assigned by rank within each division
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="pointsMode"
                checked={!autoCalculatePoints}
                onChange={() => setAutoCalculatePoints(false)}
                className="w-4 h-4 text-amber-600"
              />
              <span className="font-inter text-sm">
                <strong>Manual entry</strong> - Enter points directly for each player
              </span>
            </label>
          </div>
        </div>

        {/* Players Table */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="font-inter font-medium text-slate-900">
              Players ({fullMatchPlayers.filter(p => p.name.trim()).length} entered)
            </Label>
            <Button
              type="button"
              onClick={addPlayerRow}
              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm px-3 py-1 text-sm font-barlow"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Row
            </Button>
          </div>

          <div className="border border-stone-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left font-barlow font-semibold text-slate-900 uppercase text-xs tracking-wide px-3 py-2">
                    Player Name
                  </th>
                  <th className="text-left font-barlow font-semibold text-slate-900 uppercase text-xs tracking-wide px-3 py-2">
                    Division
                  </th>
                  <th className="text-left font-barlow font-semibold text-slate-900 uppercase text-xs tracking-wide px-3 py-2 w-24">
                    Score
                  </th>
                  {!autoCalculatePoints && (
                    <th className="text-left font-barlow font-semibold text-slate-900 uppercase text-xs tracking-wide px-3 py-2 w-24">
                      Points
                    </th>
                  )}
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {fullMatchPlayers.map((player, index) => (
                  <tr key={player.id} className="border-t border-stone-100">
                    <td className="px-3 py-2">
                      <Select
                        value={player.name}
                        onValueChange={(value) => updatePlayerRow(index, 'name', value)}
                      >
                        <SelectTrigger className="bg-white border-stone-300 rounded-sm h-10 font-inter">
                          <SelectValue placeholder="Select player..." />
                        </SelectTrigger>
                        <SelectContent>
                          {registeredUsers.map((u) => (
                            <SelectItem key={u.id} value={u.name}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={player.division}
                        onValueChange={(value) => updatePlayerRow(index, 'division', value)}
                      >
                        <SelectTrigger className="bg-white border-stone-300 rounded-sm h-10 font-inter">
                          <SelectValue placeholder="Division..." />
                        </SelectTrigger>
                        <SelectContent>
                          {configs.map((c) => (
                            <SelectItem key={c.division} value={c.division}>
                              {c.division}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={player.score}
                        onChange={(e) => updatePlayerRow(index, 'score', e.target.value)}
                        className="bg-white border-stone-300 rounded-sm h-10 font-mono text-center"
                        placeholder="0"
                      />
                    </td>
                    {!autoCalculatePoints && (
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          value={player.manualPoints}
                          onChange={(e) => updatePlayerRow(index, 'manualPoints', e.target.value)}
                          className="bg-white border-stone-300 rounded-sm h-10 font-mono text-center"
                          placeholder="0"
                          min="-100"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      {fullMatchPlayers.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removePlayerRow(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-stone-500 mt-2 font-inter">
            {autoCalculatePoints 
              ? "Players will be ranked by score within each division. Points are assigned based on division point rules."
              : "Enter the exact points for each player. Use negative values for penalties."}
          </p>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          data-testid="full-match-submit"
          disabled={submittingMatch}
          className="w-full bg-amber-600 text-white hover:bg-amber-700 rounded-sm uppercase tracking-wider font-bold h-12 font-barlow"
        >
          {submittingMatch ? (
            <>Processing...</>
          ) : (
            <>
              <Target className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Create Match
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
